import moment from 'moment';
// Mirror of libs/packages/car-schema/src/schema/data/parts/Guarantee.ts (fe-main doesn't bundle
// car-schema/class-validator) — keep both lists in sync; CBD rejects any other value with a 409.
export const GUARANTEE_COMPONENTS = [
    'engine', 'gearbox', 'drivetrain', 'tractionBattery', 'electricMotor', 'electrics', 'bodywork', 'paint', 'bumper',
    'wheels', 'tyres', 'brakes', 'suspension', 'exhaust', 'airConditioning', 'infotainment', 'other',
];
export const GUARANTEE_PROVIDER_TYPES = ['producer', 'dealer', 'autoDe', 'thirdParty', 'unknown'];
export const GUARANTEE_TYPES = ['durability', 'component', 'extended', 'other', 'unknown'];
// form values of coversEntireVehicle: '' = null (unknown), 'vehicle' = true, 'component' = false
export const GUARANTEE_COVERAGES = ['', 'vehicle', 'component'];
// entries entered or changed on this page count as the dealer's backoffice input
const EDIT_PAGE_SOURCE_TYPE = 'dealerBackoffice';
const DATE_FORMAT = 'YYYY-MM-DD';
const FORM_KEYS = [
    'coversEntireVehicle', 'components', 'componentDescription', 'providerType', 'providerName', 'guaranteeType',
    'freeOfCharge', 'durationMonths', 'mileageLimitKm', 'startDate', 'endDate', 'modelIdentifier', 'termsUrl',
    'description', 'informationMadeAvailableToTrader', 'usedVehicleApplicabilityConfirmed', 'sourceType',
];
export const emptyGuaranteeFormItem = () => ({
    coverage: '',
    components: [],
    componentDescription: '',
    providerType: '',
    providerName: '',
    guaranteeType: '',
    freeOfCharge: '',
    durationMonths: '',
    mileageLimitKm: '',
    startDate: '',
    endDate: '',
    modelIdentifier: '',
    termsUrl: '',
    description: '',
    informationMadeAvailableToTrader: '',
    usedVehicleApplicabilityConfirmed: '',
    sourceType: EDIT_PAGE_SOURCE_TYPE,
    passthrough: {},
});
// marks an entry as edited on this page
export const withEditPageSource = (item) => ({ ...item, sourceType: EDIT_PAGE_SOURCE_TYPE });
const str = (value) => (value === null || value === undefined ? '' : String(value));
const dateToForm = (value) => (Number.isInteger(value) ? moment.unix(value).utc().format(DATE_FORMAT) : '');
const dateFromForm = (value) => {
    const date = moment.utc(value, DATE_FORMAT, true);
    return value && date.isValid() ? date.unix() : null;
};
const intFromForm = (value) => (/^\d+$/.test(value.trim()) ? Number.parseInt(value.trim(), 10) : null);
const coverageToForm = (value) => (value === true ? 'vehicle' : value === false ? 'component' : '');
const triStateToForm = (value) => (value === true ? 'yes' : value === false ? 'no' : '');
const triStateFromForm = (value) => (value === 'yes' ? true : value === 'no' ? false : null);
export const guaranteeItemsFromCar = (items) => {
    if (!Array.isArray(items))
        return [];
    return items.map((item) => {
        const passthrough = {};
        Object.keys(item || {}).forEach((key) => {
            if (!FORM_KEYS.includes(key))
                passthrough[key] = item[key];
        });
        return {
            coverage: coverageToForm(item.coversEntireVehicle),
            components: Array.isArray(item.components) ? [...item.components] : [],
            componentDescription: str(item.componentDescription),
            providerType: str(item.providerType),
            providerName: str(item.providerName),
            guaranteeType: str(item.guaranteeType),
            freeOfCharge: triStateToForm(item.freeOfCharge),
            durationMonths: str(item.durationMonths),
            mileageLimitKm: str(item.mileageLimitKm),
            startDate: dateToForm(item.startDate),
            endDate: dateToForm(item.endDate),
            modelIdentifier: str(item.modelIdentifier),
            termsUrl: str(item.termsUrl),
            description: str(item.description),
            informationMadeAvailableToTrader: triStateToForm(item.informationMadeAvailableToTrader),
            usedVehicleApplicabilityConfirmed: triStateToForm(item.usedVehicleApplicabilityConfirmed),
            sourceType: str(item.sourceType),
            passthrough,
        };
    });
};
// an entry the user added but never filled in — dropped on save instead of failing validation
export const isBlankGuaranteeItem = (item) => {
    const { sourceType, passthrough, ...fields } = item;
    return !Object.keys(passthrough).length
        && Object.values(fields).every((value) => (Array.isArray(value) ? !value.length : !value.trim()));
};
export const validateGuaranteeItem = (item) => {
    const errors = {};
    if (item.coverage === 'component' && !item.components.length && !item.componentDescription.trim()) {
        errors.components = 'componentsRequired';
    }
    const durationMonths = intFromForm(item.durationMonths);
    if (item.durationMonths.trim() && (durationMonths === null || durationMonths < 1)) {
        errors.durationMonths = 'positiveInteger';
    }
    if (item.mileageLimitKm.trim() && intFromForm(item.mileageLimitKm) === null) {
        errors.mileageLimitKm = 'positiveInteger';
    }
    if (item.startDate && dateFromForm(item.startDate) === null) {
        errors.startDate = 'invalidDate';
    }
    if (item.endDate && dateFromForm(item.endDate) === null) {
        errors.endDate = 'invalidDate';
    }
    if (!errors.startDate && !errors.endDate && item.startDate && item.endDate && dateFromForm(item.endDate) < dateFromForm(item.startDate)) {
        errors.endDate = 'endBeforeStart';
    }
    if (item.termsUrl.trim() && !/^https?:\/\/\S+$/i.test(item.termsUrl.trim())) {
        errors.termsUrl = 'invalidUrl';
    }
    return errors;
};
export const guaranteeItemsToCar = (items) => items
    .filter((item) => !isBlankGuaranteeItem(item))
    .map((item) => {
    const isComponent = item.coverage === 'component';
    const result = {
        ...item.passthrough,
        coversEntireVehicle: item.coverage === 'vehicle' ? true : isComponent ? false : null,
        // components are kept when coverage is unknown: they may be all the source told us
        components: item.coverage === 'vehicle' ? [] : item.components.filter((component) => GUARANTEE_COMPONENTS.includes(component)),
        componentDescription: item.coverage === 'vehicle' ? '' : item.componentDescription.trim(),
        providerType: item.providerType,
        providerName: item.providerName.trim(),
        guaranteeType: item.guaranteeType,
        freeOfCharge: triStateFromForm(item.freeOfCharge),
        durationMonths: intFromForm(item.durationMonths),
        mileageLimitKm: intFromForm(item.mileageLimitKm),
        startDate: dateFromForm(item.startDate),
        endDate: dateFromForm(item.endDate),
        modelIdentifier: item.modelIdentifier.trim(),
        termsUrl: item.termsUrl.trim(),
        description: item.description.trim(),
        informationMadeAvailableToTrader: triStateFromForm(item.informationMadeAvailableToTrader),
        usedVehicleApplicabilityConfirmed: triStateFromForm(item.usedVehicleApplicabilityConfirmed),
        sourceType: item.sourceType || EDIT_PAGE_SOURCE_TYPE,
    };
    // unknown / not provided values are left out of the car document instead of stored as ''/null
    Object.keys(result).forEach((key) => {
        const value = result[key];
        if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
            delete result[key];
        }
    });
    return result;
});
